package net.zagart.ai.core.app

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import net.zagart.ai.core.client.deepseek.DeepseekClient
import net.zagart.ai.core.client.deepseek.DeepseekConfig
import net.zagart.ai.core.feature.story.StoryChoice
import net.zagart.ai.core.feature.story.StoryConfig
import net.zagart.ai.core.feature.story.StoryEngine
import net.zagart.ai.core.feature.story.StoryState

data class Labels(
    val headline: String,
    val genreField: String,
    val defaultGenre: String,
    val wishesField: String,
    val languageLabel: String,
    val english: String,
    val russian: String,
    val maxTokensField: String,
    val startButton: String,
    val chooseHeader: String,
    val restartButton: String,
    val errorPrefix: String,
    val errorFallback: String,
) {
    companion object {
        fun forLanguage(lang: String) = when (lang) {
            "russian" -> Labels(
                headline = "Интерактивная история",
                genreField = "Жанр",
                defaultGenre = "фэнтези",
                wishesField = "Пожелания по сюжету",
                languageLabel = "Язык",
                english = "Английский",
                russian = "Русский",
                maxTokensField = "Макс. токенов",
                startButton = "Начать историю",
                chooseHeader = "Выберите:",
                restartButton = "Заново",
                errorPrefix = "Ошибка: ",
                errorFallback = "Неизвестная ошибка",
            )

            else -> Labels(
                headline = "Interactive Story",
                genreField = "Genre",
                defaultGenre = "fantasy",
                wishesField = "Plot wishes",
                languageLabel = "Language",
                english = "English",
                russian = "Russian",
                maxTokensField = "Max tokens",
                startButton = "Start Story",
                chooseHeader = "Choose:",
                restartButton = "Restart",
                errorPrefix = "Error: ",
                errorFallback = "Unknown error",
            )
        }
    }
}

@Composable
fun StoryScreen(deepseekConfig: DeepseekConfig, appVersion: Int = 0) {
    AppTheme {
        var language by remember { mutableStateOf("russian") }
        val labels = remember(language) { Labels.forLanguage(language) }
        var genre by remember(language) { mutableStateOf(labels.defaultGenre) }
        var plotWishes by remember { mutableStateOf("") }
        var maxTokens by remember { mutableStateOf(StoryConfig().maxHistoryTokens.toString()) }
        var storyState by remember { mutableStateOf<StoryState?>(null) }
        var engine by remember { mutableStateOf<StoryEngine?>(null) }
        var loading by remember { mutableStateOf(false) }
        var error by remember { mutableStateOf<String?>(null) }
        val scope = rememberCoroutineScope()
        val scrollState = rememberScrollState()

        LaunchedEffect(loading) {
            scrollState.scrollTo(0)
        }

        fun startStory() {
            scope.launch {
                loading = true
                error = null
                try {
                    val client = DeepseekClient(deepseekConfig)
                    val eng = StoryEngine(
                        client,
                        StoryConfig(
                            genre = genre,
                            language = language,
                            plotWishes = plotWishes,
                            maxHistoryTokens = maxTokens.toIntOrNull()
                                ?: StoryConfig().maxHistoryTokens
                        )
                    )
                    engine = eng
                    storyState = eng.start()
                } catch (e: Exception) {
                    error = e.message ?: labels.errorFallback
                } finally {
                    loading = false
                }
            }
        }

        fun chooseChoice(choice: StoryChoice) {
            scope.launch {
                loading = true
                error = null
                try {
                    storyState = engine?.choose(choice)
                } catch (e: Exception) {
                    error = e.message ?: labels.errorFallback
                } finally {
                    loading = false
                }
            }
        }

        fun restart() {
            engine?.reset()
            storyState = null
            error = null
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .verticalScroll(scrollState),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(labels.headline, style = MaterialTheme.typography.headlineMedium)
                    Spacer(Modifier.width(12.dp))
                    Text(
                        "v$appVersion",
                        style = MaterialTheme.typography.titleSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                if (loading && storyState != null) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }

                if (storyState == null) {
                    OutlinedTextField(
                        value = genre,
                        onValueChange = { genre = it },
                        label = { Text(labels.genreField) },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    OutlinedTextField(
                        value = plotWishes,
                        onValueChange = { plotWishes = it },
                        label = { Text(labels.wishesField) },
                        singleLine = false,
                        minLines = 2,
                        maxLines = 4,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Text(labels.languageLabel, style = MaterialTheme.typography.titleSmall)

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        FilterChip(
                            selected = language == "english",
                            onClick = { language = "english" },
                            label = { Text(labels.english) }
                        )
                        FilterChip(
                            selected = language == "russian",
                            onClick = { language = "russian" },
                            label = { Text(labels.russian) }
                        )
                    }

                    OutlinedTextField(
                        value = maxTokens,
                        onValueChange = { maxTokens = it.filter { c -> c.isDigit() } },
                        label = { Text(labels.maxTokensField) },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth()
                    )

                    Button(
                        onClick = ::startStory,
                        enabled = !loading
                    ) {
                        if (loading) CircularProgressIndicator(modifier = Modifier.size(20.dp))
                        else Text(labels.startButton)
                    }
                } else {
                    val state = storyState!!

                    Text(
                        text = state.narrativeText,
                        style = MaterialTheme.typography.bodyLarge
                    )

                    if (!state.isComplete) {
                        Text(labels.chooseHeader, style = MaterialTheme.typography.titleSmall)

                        state.choices.forEach { choice ->
                            OutlinedButton(
                                onClick = { chooseChoice(choice) },
                                enabled = !loading,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Text(choice.text)
                            }
                        }
                    } else {
                        Button(onClick = ::restart) {
                            Text(labels.restartButton)
                        }
                    }
                }

                error?.let { msg ->
                    Text("${labels.errorPrefix}$msg", color = MaterialTheme.colorScheme.error)
                }

                if (loading && storyState != null) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        }
    }
}
